/**
 * @description
 * Provides basic functions to work with Mosy messages
 * - filtering
 * - serialization to binary format
 * - deserialization from binary format
 * - serialization to text format
 * - deserialization from text format
 * - masking parameters
 */

const ms = module.exports = {}
const MESSAGE_RE = /^(.*?)>(.*?)\.(\w+)(?:\?(.*))?$/
const ADDRESS_RE = /^(?:(\w*)([:@~]))?(#?\w+)(?:\$(\w*))?$/

function toString(str) {
  if(str===null || typeof (str)==='undefined') return ''
  return typeof (str)==='string' ? str : String(str)
} // toString

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Serializes Mosy message to string
 * @param {object} msg
 * @param {function|null} replace_fcn - function used for custom replacement
 */
ms.msg2string = function(msg, replace_fcn) {
  if(typeof msg==='string') return MESSAGE_RE.exec(msg) ? msg : ''
  if(!msg || typeof (msg)!=='object') return ''
  const sender = ms.address2string(msg.sender)
  const target = ms.address2string(msg.target)
  const action = toString(msg.action)
  const params = ms.params2string(msg.params, replace_fcn)
  return sender+'>'+target+'.'+action+(params?'?'+params:'')
} // msg2string

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Deserializes Mosy message from string
 * @param {object} str
 */
ms.string2msg = function(str) {
  if(str && typeof str==='object') {
    const sender = ms.address2hash(str.sender)
    const target = ms.address2hash(str.target)
    const action = toString(str.action)
    const params = ms.string2params(str.params)
    return { sender, target, action, params }
  }else{
    const m = MESSAGE_RE.exec(str)
    if(!m) return null
    let [, sender, target, action, params] = m
    action = toString(action)
    if(!action) return null
    sender = ms.address2hash(sender)
    target = ms.address2hash(target)
    params = ms.string2params(params)
    return { sender, target, action, params }
  }
} // msg2string

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Serializes Mesy sender/target object to string address
 * @param {object|string} hash
 */
ms.address2string = function(hash) {
  if(typeof (hash)==='string') {
    if(ADDRESS_RE.exec(hash)) return hash
    else return ''
  }
  if(typeof (hash)==='number') return String(hash)
  if(!hash || typeof (hash)!=='object') return ''
  const scope = toString(hash.scope)
  let str = scope==='~' ? toString(hash.instance_id)+'~' : scope ? toString(hash.app_id) + scope : ''
  str += toString(hash.listener_id)
  const request_id = toString(hash.request_id)
  if(request_id.length>0) str += '$'+request_id
  return str
} // address2string

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Deserializes Mesy sender/target object from string address
 * @param {object|string} str
 */
ms.address2hash = function(str) {
  if(!str) return null

  // we are already object, so just ensure all fields are properly filled
  if(typeof (str)==='object') {
    const listener_id = toString(str.listener_id)
    if(!listener_id) return null
    if(str.scope==='~') delete str.app_id
    return {
      ...str,
      app_id: toString(str.app_id),
      instance_id: toString(str.instance_id),
      scope: toString(str.scope),
      listener_id,
      request_id: toString(str.request_id),
    }
  }

  // parse address from string
  const m = ADDRESS_RE.exec(str)
  if(!m) return null
  if(m[2]==='~') {
    return {
      app_id: '',
      instance_id: toString(m[1]),
      scope: toString(m[2]),
      listener_id: m[3],
      request_id: toString(m[4]),
    }
  }else{
    return {
      app_id: toString(m[1]),
      instance_id: '',
      scope: toString(m[2]),
      listener_id: m[3],
      request_id: toString(m[4]),
    }
  }
} // address2hash

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Deserializes Mosy message parameters from string
 * @param {object} src
 */
ms.string2params = function(str) {
  if(!str) return {}
  try{
    return JSON.parse(str)
  }catch(e) {
    return {}
  }
} // string2params

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Serializes Mosy message parameters to string
 * @param {object} src
 * @param {function|null} replace_fcn - function used for custom replacement
 */
ms.params2string = function(src, replace_fcn) {
  if(!src || typeof (src)!=='object' || Object.keys(src).length===0) return ''
  src = ms.cloneParams(src)
  replace_fcn = replace_fcn || function(key, value) {
    if(key) return value
    if(value instanceof Date) return value.valueOf()
    if(value instanceof ArrayBuffer || ArrayBuffer.isView(value)) return value.byteLength+' bytes'
    return null
  }
  return JSON.stringify(ms.deepReplace(src, replace_fcn))
} // params2string

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Clones Mosy parameters with some specific expectations:
 * - we just link not clone functions, errors, RegExps and HTML objects
 * @param {object} src
 */
ms.cloneParams = function(src) {
  if(!src || typeof src !== 'object') return src
  if(src instanceof Array) return src.map(x=>ms.cloneParams(x))
  if(src instanceof Date) return new Date(src.getTime())
  if(src instanceof ArrayBuffer || ArrayBuffer.isView(src)) return src.slice()
  if(/^\[object (Function|Error|RegExp|Window|HTML.*)\]$/.test(Object.prototype.toString.call(src))) return src
  return Object.fromEntries(Object.entries(src).map(([k, v])=>[k, ms.cloneParams(v)]))
} // cloneParams

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Clears Mosy parameters with some specific expectations:
 * - remove non-cloneable functions, errors, RegExps and HTML objects
 * @param {object} src
 * @param {function|null} replace_fcn - function used for custom replacement
 */
ms.deepReplace = function(src, replace_fcn) {
  if(!src || typeof src !== 'object') return src
  if(src instanceof Array) return src.map(v=>ms.deepReplace(v, replace_fcn))
  const proto_name = Object.prototype.toString.call(src)
  if(proto_name==='[object Object]') return Object.fromEntries(Object.entries(src).map(([k, v])=>[k, ms.deepReplace(replace_fcn(k, v), replace_fcn)]))
  return replace_fcn(null, src, proto_name.replace(/^\[object\s*(.*)\]$/, '$1')) // special object within array
} // deepReplace

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Converts microseconds to Date() object
 * @param {integer} us - microseconds since epoch
 */
ms.us2date = function(us) {
  return new Date(Math.floor(us/1000))
} // us2date

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Converts microseconds to string
 * @param {integer} us - microseconds since epoch
 */
ms.us2string = function(us) {
  try{
    const d = new Date(Math.floor(us/1000))
    if(isNaN(d)) return ''
    return d.toLocaleTimeString('lt-lt')+'.'+String(us % 1_000_000).padStart(6, '0')
  }catch(e) {
    return ''
  }
} // us2string

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Converts microseconds to string
 * @param {integer} us - microseconds since epoch
 */
const ACTION_ORDER = { mmH264: 1, mmJPEG: 2 }
const NALTYPE_ORDER = { 1: 4, 5: 3, 7: 1, 8: 2, P: 4, I: 3, SPS: 1, PPS: 2 }
ms.compare = function(a, b) {
  let cmp = a.params.event_dt - b.params.event_dt
  if(cmp) return cmp
  cmp = (ACTION_ORDER[a.action] || 999) - (ACTION_ORDER[b.action] || 999)
  if(cmp) return cmp
  cmp = (a.params.channel || 0) - (b.params.channel || 0)
  if(cmp) return cmp
  if(a.action==='mmH264' && b.action==='mmH264') {
    cmp = (NALTYPE_ORDER[a.params.nal_type] || 999) - (NALTYPE_ORDER[b.params.nal_type] || 999)
    if(cmp) return cmp
    cmp = a.params.channel - b.params.channel
    if(cmp) return cmp
    return a.params.id - b.params.id
  }
  return 0
} // compare
